import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  AmountDisplay,
  FiatValueDisplay,
  useBondingCurve,
  useDetailedBalance,
  useFixedAmountInput,
} from '@onomy/react-hub';
import { OnomyHubConstants } from '@onomy/hub';

import {
  // WarningDiv,
  SellBtn,
  ExchangeButton,
  Sending,
  ExchangeInput,
  MaxBtn,
  Receiving,
  ReceivingValue,
  SendingBoxLabel,
  SymbolDisplay,
} from './exchangeStyles';
import { useModal } from 'context/modal/ModalContext';
import { responsive } from 'theme/constants';
import { useExchange } from 'context/exchange/ExchangeContext';

const ModalTrigger = styled.div`
  display: none;

  @media screen and (max-width: ${responsive.smartphoneLarge}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`;

const ExchangeModalWrapper = styled.div`
  width: 100%;
  height: 100%;

  background-color: ${props => props.theme.colors.bgDarkest};

  header {
    padding: 20px 20px 0;

    background-color: ${props => props.theme.colors.bgNormal};
  }
`;

const ModalHeader = styled.header`
  display: flex;
  align-items: center;

  h6 {
    margin: 0 auto;

    font-size: 16px;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  padding: 56px 20px;
`;

const HeaderInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media screen and (max-width: ${responsive.laptop}) {
    gap: 5px;
  }

  & + & {
    margin-left: 56px;

    @media screen and (max-width: ${responsive.laptop}) {
      margin-left: 32px;
    }
  }

  > strong {
    color: ${props => props.theme.colors.textThirdly};
    font-weight: 400;

    @media screen and (max-width: ${responsive.smartphoneLarge}) {
      font-size: 10px;
    }
  }
`;

const HeaderInfoItemValue = styled.div`
  @media screen and (max-width: ${responsive.smartphoneLarge}) {
    display: flex;
    flex-direction: column;

    gap: 5px;
  }

  > strong {
    margin-right: 12px;

    color: ${props => props.theme.colors.textPrimary};
    font-family: 'Bebas Neue', sans-serif;
    font-size: 24px;

    @media screen and (max-width: ${responsive.laptop}) {
      font-size: 20px;
    }

    @media screen and (max-width: ${responsive.smartphoneLarge}) {
      font-size: 18px;
    }
  }
`;

const ModalInfo = styled.div`
  display: flex;
  justify-content: space-between;

  padding: 32px 20px;

  background-color: ${props => props.theme.colors.bgNormal};
`;

const ModalBtn = styled.button`
  width: 44px;
  height: 44px;

  border-radius: 8px;
  border: none;
  background-color: ${props => props.theme.colors.bgHighlightBorder};

  color: #84809a;

  cursor: pointer;
`;

const modalOverride = {
  content: {
    padding: 0,
    border: 'none',
    borderRadius: 0,

    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
};

export function ExchangeMobile() {
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const {
    ethBalance,
    bNomBalance,
    ethBidAmountFixed,
    toggleSellMaxAmount,
    bNomQuote,
    ethQuote,
    bNomAskAmountFixed,
    setEthBidAmountFixed,
    setBNomAskAmountFixed,
  } = useExchange();
  const [bNOM] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, '1');
  const { bNomPerEth } = useBondingCurve();

  const { onShowConfirmBuy, onShowConfirmSell } = useModal();

  const [ethBidDisplayValue, setEthBidDisplayValue] = useFixedAmountInput({
    value: ethBidAmountFixed,
    onChangeValue: setEthBidAmountFixed,
    meta: ethBalance.asset.meta,
  });

  const [bNomBidDisplayValue, setBNomBidDisplayValue] = useFixedAmountInput({
    value: bNomAskAmountFixed,
    onChangeValue: setBNomAskAmountFixed,
    meta: ethBalance.asset.meta,
  });

  return (
    <ModalTrigger>
      <ExchangeButton
        data-testid="exchanges-modals-buy-button"
        onClick={() => setBuyModalOpen(true)}
      >
        Buy bNOM
      </ExchangeButton>

      <SellBtn data-testid="exchanges-modals-sell-button" onClick={() => setSellModalOpen(true)}>
        Sell bNOM
      </SellBtn>

      <Modal
        ariaHideApp={false}
        isOpen={buyModalOpen}
        style={modalOverride}
        onRequestClose={() => setBuyModalOpen(false)}
      >
        <ExchangeModalWrapper>
          <ModalHeader>
            <ModalBtn onClick={() => setBuyModalOpen(false)}>
              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
            </ModalBtn>
            <h6>Buy bNOM</h6>
            <ModalBtn
              onClick={() => {
                setSellModalOpen(!sellModalOpen);
                setBuyModalOpen(!buyModalOpen);
              }}
            >
              <FontAwesomeIcon icon={faExchangeAlt as IconProp} />
            </ModalBtn>
          </ModalHeader>

          <ModalInfo data-testid="buy-nom-modal-info">
            <HeaderInfoItem>
              <strong>Eth Balance</strong>
              <HeaderInfoItemValue>
                <strong>
                  <AmountDisplay balance={ethBalance} SymbolComponent={null} />
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
            <HeaderInfoItem>
              <strong>bNOM Balance</strong>
              <HeaderInfoItemValue>
                <strong>
                  <AmountDisplay balance={bNomBalance} SymbolComponent={null} />
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
            <HeaderInfoItem>
              <strong>NOM / USDT</strong>
              <HeaderInfoItemValue>
                <strong>
                  <FiatValueDisplay value={bNOM.fiatPrice} />
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
            <HeaderInfoItem>
              <strong>NOM / ETH</strong>
              <HeaderInfoItemValue>
                <strong>
                  <span>{bNomPerEth.toFixed(8)}</span>
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
          </ModalInfo>

          <FormWrapper data-testid="buy-nom-modal-results">
            <strong>Buy bNOM</strong>
            <Sending>
              {ethBidDisplayValue === '' && <strong>I&apos;m buying for</strong>}
              <ExchangeInput
                type="text"
                name="ETHValue"
                onChange={e => setEthBidDisplayValue(e.target.value)}
                value={ethBidDisplayValue}
                placeholder="0.00"
                autoComplete="off"
              />
              <SendingBoxLabel>
                <strong style={{ marginLeft: '16px' }}>ETH</strong>
              </SendingBoxLabel>
            </Sending>
            {/*buyIsMaxAmount ? (
              <WarningDiv>WARNING: ETH Gas is needed to sell or bridge after purchase!</WarningDiv>
            ) : null*/}
            <Receiving>
              <strong>You will receive</strong>
              <ReceivingValue>
                <AmountDisplay balance={bNomQuote} SymbolComponent={SymbolDisplay} />
              </ReceivingValue>
            </Receiving>
            <div>
              <ExchangeButton onClick={() => onShowConfirmBuy()}>Buy NOM</ExchangeButton>
            </div>
          </FormWrapper>
        </ExchangeModalWrapper>
      </Modal>

      <Modal
        ariaHideApp={false}
        isOpen={sellModalOpen}
        style={modalOverride}
        onRequestClose={() => setSellModalOpen(false)}
      >
        <ExchangeModalWrapper>
          <ModalHeader>
            <ModalBtn onClick={() => setSellModalOpen(false)}>
              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
            </ModalBtn>
            <h6>Sell bNOM</h6>
            <ModalBtn
              onClick={() => {
                setSellModalOpen(!sellModalOpen);
                setBuyModalOpen(!buyModalOpen);
              }}
            >
              <FontAwesomeIcon icon={faExchangeAlt as IconProp} />
            </ModalBtn>
          </ModalHeader>

          <ModalInfo data-testid="sell-nom-modal-info">
            <HeaderInfoItem>
              <strong>Eth Balance</strong>
              <HeaderInfoItemValue>
                <strong>
                  <AmountDisplay balance={ethBalance} SymbolComponent={null} />
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
            <HeaderInfoItem>
              <strong>bNOM Balance</strong>
              <HeaderInfoItemValue>
                <strong>
                  <AmountDisplay balance={bNomBalance} SymbolComponent={null} />
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
            <HeaderInfoItem>
              <strong>NOM / USDT</strong>
              <HeaderInfoItemValue>
                <strong>
                  <FiatValueDisplay value={bNOM.fiatPrice} />
                </strong>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
            <HeaderInfoItem>
              <strong>NOM / ETH</strong>
              <HeaderInfoItemValue>
                <span>{bNomPerEth.toFixed(8)}</span>
              </HeaderInfoItemValue>
            </HeaderInfoItem>
          </ModalInfo>

          <FormWrapper data-testid="sell-nom-modal-results">
            <strong>Sell bNOM</strong>
            <Sending>
              {bNomBidDisplayValue === '' && <strong>I&apos;m selling</strong>}
              <ExchangeInput
                type="text"
                name="WNOMValue"
                onChange={e => setBNomBidDisplayValue(e.target.value)}
                value={bNomBidDisplayValue}
                placeholder="0.00"
                autoComplete="off"
              />
              <SendingBoxLabel>
                <strong style={{ marginLeft: '16px' }}>bNOM</strong>
                <MaxBtn onClick={toggleSellMaxAmount} name="bNOMValue">
                  Max
                </MaxBtn>
              </SendingBoxLabel>
            </Sending>
            <Receiving>
              <strong>You will receive</strong>
              <ReceivingValue>
                <AmountDisplay balance={ethQuote} SymbolComponent={SymbolDisplay} />
              </ReceivingValue>
            </Receiving>
            <div>
              <SellBtn onClick={() => onShowConfirmSell()}>Sell bNOM</SellBtn>
            </div>
          </FormWrapper>
        </ExchangeModalWrapper>
      </Modal>
    </ModalTrigger>
  );
}
